import { Spinner } from '@kcftech/design-system';
import { IssueHardwareType } from '@kcftech/prometheustypescriptapiclient';
import { SearchInput } from 'components/search/SearchInput';
import { usePermissions } from 'contexts/PermissionsContext';
import { useSet } from 'custom_hooks/useSet';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { FaultTypeNode } from 'pages/Desk/issues/fault_type/FaultTypeNode';
import {
    FaultTypePopupContentContainer,
    FaultTypePopupContentFooter,
    FaultTypePopupContentTree,
} from 'pages/Desk/issues/fault_type/styled';
import {
    filterFaultTypes,
    IssueFaultType,
    useFaultTypes,
} from 'pages/Desk/issues/fault_type/useFaultTypes';
import { IssueMode } from 'pages/Desk/issues/shared';
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { StyledButton } from 'shared/button/Button';
import { DataTree } from 'shared/data_tree/DataTree';
import {
    Tree,
    TreeExpansionConfig,
    TreeNodeRenderer,
    TreeSelectionConfig,
} from 'shared/data_tree/types';
import { Popup, PopupAnchorRef } from 'shared/popup/Popup';
import { useLimitMaxHeightToScreenRef } from 'shared/triggered_popup/useLimitMaxHeightToScreenRef';

export type FaultTypePopupProps = Readonly<{
    anchorRef: PopupAnchorRef;
    initialFaultIds: string[];
    issueMode: IssueMode;
    disableRootLevel: boolean;
    hardwareType?: IssueHardwareType;
    isCreating?: boolean;
    hideInternal?: boolean;
    onClose: (newFaults?: IssueFaultType[]) => void;
}>;

const FaultTypePopup = forwardRef<HTMLDivElement, FaultTypePopupProps>(
    ({ hideInternal, ...props }: FaultTypePopupProps, ref: React.Ref<HTMLDivElement>) => {
        const {
            issue: { canViewInternalIssues },
        } = usePermissions();

        const { faultTypesTree } = useFaultTypes({
            mode: props.issueMode,
            hardwareType: props.hardwareType,
            isInternalOnly:
                hideInternal || !canViewInternalIssues
                    ? // cannot (or don't want to) view internal Fault Types -- only
                      // include Fault Types NOT marked internalOnly
                      false
                    : // can view internal Fault Types, no need to filter anything out
                      undefined,
            forManualCreation: props.isCreating,
        });

        if (faultTypesTree !== undefined) {
            return (
                <FaultTypePopupImpl
                    {...props}
                    faultTypesTree={faultTypesTree}
                    ref={ref}
                />
            );
        }
        return (
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            >
                <Spinner />
            </div>
        );
    }
);

type FaultTypePopupImplProps = FaultTypePopupProps & {
    faultTypesTree: Tree<IssueFaultType>;
};

const FaultTypePopupImpl = forwardRef(
    (
        {
            anchorRef,
            initialFaultIds,
            faultTypesTree,
            disableRootLevel,
            onClose,
        }: FaultTypePopupImplProps,
        ref: React.Ref<HTMLDivElement>
    ) => {
        const [filterText, setFilterText] = useState('');
        const [selectedIds, setSelectedIds] = useSet<string>(
            () => new Set(initialFaultIds)
        );

        const [expandedIds, setExpandedIds] = useSet<string>(() => {
            // If there's only one root node, and it can't be selected, default it to open
            if (
                initialFaultIds.length === 0 &&
                faultTypesTree.rootNodes.length === 1 &&
                disableRootLevel
            ) {
                return new Set(faultTypesTree.rootNodes.map((node) => node.id));
            }

            // Set initial expanded nodes to parents of selected nodes
            const selectedNodes = initialFaultIds
                .filter(faultTypesTree.has)
                .map(faultTypesTree.get);
            const parentIds = selectedNodes.flatMap((node) =>
                node.hierarchy.map((parent) => parent.id)
            );
            return new Set(parentIds);
        });
        const isFullScreen = useIsViewportAtMost(MediaSize.small);

        const finalFilterText = useMemo(() => filterText.trim().toLowerCase(), [
            filterText,
        ]);

        const filteredFaultTypesTree = useMemo(
            () =>
                finalFilterText
                    ? filterFaultTypes(faultTypesTree, finalFilterText)
                    : undefined,
            [faultTypesTree, finalFilterText]
        );

        const expandable = useMemo<TreeExpansionConfig<IssueFaultType>>(
            () => ({
                isExpanded: (node) => expandedIds.has(node.id),
                onChange: (event) => setExpandedIds.toggle(event.node.id),
            }),
            [expandedIds, setExpandedIds]
        );

        const selectable = useMemo<TreeSelectionConfig<IssueFaultType>>(
            () => ({
                isSelected: (node) => selectedIds.has(node.id),
                canSelect: (node) => !disableRootLevel || node.isLeaf,
                onChange: (event) => {
                    if (event.isSelected) {
                        const parentIds = event.node.hierarchy.map((node) => node.id);
                        const childIds = event.node.allChildren.map((node) => node.id);
                        const idsToDeselect = [...parentIds, ...childIds];

                        setSelectedIds.remove(...idsToDeselect);
                        setSelectedIds.add(event.node.id);
                    } else {
                        setSelectedIds.remove(event.node.id);
                    }
                },
                selectionStyle: 'checkbox',
            }),
            [disableRootLevel, selectedIds, setSelectedIds]
        );

        const onFilterInputChange = (newFilterText: string) => {
            setFilterText(newFilterText);
        };

        const renderNode = useCallback<TreeNodeRenderer<IssueFaultType>>(
            (info) => <FaultTypeNode node={info.node} filterText={finalFilterText} />,
            [finalFilterText]
        );

        const limitMaxHeightRef = useLimitMaxHeightToScreenRef();

        useEffect(() => {
            // Set expanded nodes if filter text changes
            if (filteredFaultTypesTree) {
                const expandedNodeIds = filteredFaultTypesTree.allNodes
                    .filter((node) =>
                        node.data.name.toLowerCase().includes(finalFilterText)
                    )
                    .flatMap((node) => node.hierarchy)
                    .map((node) => node.id);

                setExpandedIds.set(() => new Set(expandedNodeIds));
            }
        }, [filteredFaultTypesTree, finalFilterText, setExpandedIds]);

        return (
            <Popup
                ref={ref}
                anchorRef={anchorRef}
                fullScreenHeader="Fault Types"
                ariaLabel="Fault Types"
                focusLock
                isFullScreen={isFullScreen}
                onClose={() => onClose()}
                test-id="FaultTypePopup"
            >
                <FaultTypePopupContentContainer ref={limitMaxHeightRef}>
                    <div>
                        <SearchInput
                            placeholder="Search Fault Types"
                            style={{ fontSize: '1.4rem' }}
                            value={filterText}
                            onChange={(event) => onFilterInputChange(event.target.value)}
                        />
                    </div>
                    <FaultTypePopupContentTree
                        data-test-id="fault-types-tree-wrapper"
                        role="listbox"
                        aria-multiselectable="true"
                    >
                        <DataTree
                            treeId="fault-types-tree"
                            data={filteredFaultTypesTree ?? faultTypesTree}
                            renderNode={renderNode}
                            expandable={expandable}
                            selectable={selectable}
                        />
                    </FaultTypePopupContentTree>
                    <FaultTypePopupContentFooter>
                        <StyledButton
                            appearance="secondary"
                            $padding="0.75rem 1rem"
                            onClick={() => {
                                onClose(undefined);
                            }}
                        >
                            Cancel
                        </StyledButton>
                        <StyledButton
                            appearance="primary"
                            $padding="0.75rem 1rem"
                            onClick={() => {
                                onClose(
                                    [...selectedIds]
                                        .filter(faultTypesTree.has)
                                        .map((id) => faultTypesTree.get(id).data)
                                );
                            }}
                        >
                            Update
                        </StyledButton>
                    </FaultTypePopupContentFooter>
                </FaultTypePopupContentContainer>
            </Popup>
        );
    }
);

export { FaultTypePopup };
